import React, { useContext, useEffect, useState, useRef } from 'react';
import {
    Table, Typography, Tag, Pagination, Modal, Card, Divider, Empty, Row, Col, message, Steps, Tooltip, Button, Space, Spin
} from 'antd';
import { EyeOutlined, ExclamationCircleOutlined, EditOutlined, FilePdfOutlined } from '@ant-design/icons';
import { LoginContext } from '../../../../context/loginContext';
import { GlobalContext } from '../../../../context/globalContext';
import { CGenerarPDF } from './CGenerarPDF';
import  CEditarPedido  from './CEditarPedido.js';
import { getFormaPagoLabel } from '../../../../utils/formaPagoLabel';

const { Text } = Typography;
const { confirm } = Modal;

const CPedidosSucursal = () => {
    const { user } = useContext(LoginContext);
    const [pedidos, setPedidos] = useState([]);
    const [total, setTotal] = useState(0);
    const [paginaActual, setPaginaActual] = useState(1);
    const [loading, setLoading] = useState(false);
    const [pedidoSeleccionado, setPedidoSeleccionado] = useState(null);
    const [estados, setEstados] = useState({});
    const [generandoPDFId, setGenerandoPDFId] = useState(null);
    const [cambiandoEstado, setCambiandoEstado] = useState(false);
    const { criteriosReemplazo, tipoPagos } = useContext(GlobalContext);
    const [editandoPedido, setEditandoPedido] = useState(null);
    const ultimoPedidoIdRef = useRef(null);

    // Sonido de notificación: Web Audio API (sin archivo). Más fuerte: dos pitidos, mayor ganancia, onda más “presente”.
    const reproducirSonido = async () => {
        try {
            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            if (!AudioContextClass) {
                console.warn('Web Audio API no está disponible');
                return;
            }

            const audioContext = new AudioContextClass();

            if (audioContext.state === 'suspended') {
                await audioContext.resume();
            }

            const t0 = audioContext.currentTime;
            const volumenPico = 0.88; // antes ~0.5; sube para que se escuche más (máx. recomendado ~0.95 para no distorsionar)

            const pitido = (inicio, frecuenciaHz, duracionSeg) => {
                const osc = audioContext.createOscillator();
                const gain = audioContext.createGain();
                osc.connect(gain);
                gain.connect(audioContext.destination);
                osc.frequency.value = frecuenciaHz;
                // 'square' es más audible que 'sine' a igual volumen percibido
                osc.type = 'square';
                gain.gain.setValueAtTime(0.001, inicio);
                gain.gain.linearRampToValueAtTime(volumenPico, inicio + 0.02);
                gain.gain.exponentialRampToValueAtTime(0.001, inicio + duracionSeg);
                osc.start(inicio);
                osc.stop(inicio + duracionSeg + 0.02);
            };

            // Dos pitidos: más fácil de notificar que uno solo
            pitido(t0, 1000, 0.22);
            pitido(t0 + 0.32, 1200, 0.22);
        } catch (error) {
            console.error('Error al reproducir sonido:', error);
        }
    };

    const fetchEstados = async () => {
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/estado-pedido`);
            const data = await res.json();
            const mapeo = {};
            data.data.forEach(e => {
                let color = 'default';
                switch (e.epe_id) {
                    case 1: color = 'gold'; break;
                    case 2: color = 'blue'; break;
                    case 3: color = 'green'; break;
                    case 4: color = 'red'; break;
                }
                mapeo[e.epe_id] = { label: e.epe_nombre, color };
            });
            setEstados(mapeo);
        } catch (err) {
            console.error('Error al obtener estados', err.message);
        }
    };

    const fetchPedidos = async (pagina = 1, silent = false) => {
        try {
            if (!silent) {
                setLoading(true);
            }
            const res = await fetch(`${process.env.REACT_APP_API_URL}/pedido/sucursal?page=${pagina}`, {
                headers: { Authorization: `Bearer ${user.token}` }
            });
            const data = await res.json();
            if (res.ok) {
                // Verificar si hay un nuevo pedido (solo en la primera página)
                if (pagina === 1 && data.pedidos && data.pedidos.length > 0) {
                    const primerPedidoId = data.pedidos[0].ped_id;
                    if (ultimoPedidoIdRef.current !== null && primerPedidoId !== ultimoPedidoIdRef.current) {
                        // Hay un nuevo pedido
                        reproducirSonido(); // Reproducir sonido de notificación
                        message.info('¡Nuevo pedido recibido!', 3);
                    }
                    ultimoPedidoIdRef.current = primerPedidoId;
                }
                setPedidos(data.pedidos);
                setTotal(data.total || 0);
                setPaginaActual(pagina);
            } else {
                console.error('Error al obtener pedidos', data.message);
            }
        } catch (err) {
            console.error(err.message);
        } finally {
            if (!silent) {
                setLoading(false);
            }
        }
    };

    const cambiarEstado = async (pedidoId, nuevoEstado) => {
        if (cambiandoEstado) return;

        try {
            setCambiandoEstado(true);
            const res = await fetch(`${process.env.REACT_APP_API_URL}/pedido/${pedidoId}/estado`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${user.token}`
                },
                body: JSON.stringify({ nuevo_estado: nuevoEstado })
            });
            if (res.ok) {
                message.success('Estado actualizado');
                fetchPedidos(paginaActual);
                if (pedidoSeleccionado) {
                    setPedidoSeleccionado(prev => ({ ...prev, ped_estado: nuevoEstado }));
                }
            } else {
                const data = await res.json();
                message.error(data.message || 'Error al actualizar estado');
            }
        } catch (err) {
            console.error(err.message);
            message.error('Error inesperado al cambiar estado');
        } finally {
            setCambiandoEstado(false);
        }
    };

    const confirmarCambioEstado = (pedidoId, estadoActual, nuevoEstado, nombreEstado) => {
        if (estadoActual === nuevoEstado) return;
        confirm({
            title: `¿Cambiar estado a "${nombreEstado}"?`,
            icon: <ExclamationCircleOutlined />,
            okText: 'Sí',
            cancelText: 'No',
            onOk() {
                cambiarEstado(pedidoId, nuevoEstado);
            }
        });
    };

    useEffect(() => {
        fetchEstados();
        fetchPedidos();
    }, []);

    // Polling para detectar nuevos pedidos cada 30 segundos
    useEffect(() => {
        // Solo hacer polling si estamos en la primera página
        if (paginaActual !== 1) return;

        const intervalId = setInterval(() => {
            // Hacer una consulta silenciosa (sin mostrar loading) para verificar nuevos pedidos
            fetchPedidos(1, true);
        }, 30000); // 30 segundos

        // Limpiar el intervalo cuando el componente se desmonte o cambie la página
        return () => clearInterval(intervalId);
    }, [paginaActual, user.token]);

    const columnas = [
        {
            title: 'Fecha',
            dataIndex: 'ped_fecha',
            width: 200,
            render: fecha => new Date(fecha).toLocaleString()
        },
        {
            title: 'Cliente',
            dataIndex: 'cli_nombre',
            render: nombre => <Text>{nombre}</Text>
        },
        {
            title: 'Estado',
            dataIndex: 'ped_estado',
            render: (estado, record) => {
                const current = estado - 1;
                const sortedEstados = Object.entries(estados).sort((a, b) => parseInt(a[0]) - parseInt(b[0]));

                return (
                    <Steps
                        current={current}
                        onChange={(step) => {
                            const nuevoEstado = step + 1;
                            if (nuevoEstado !== estado) {
                                confirmarCambioEstado(record.ped_id, estado, nuevoEstado, estados[nuevoEstado]?.label);
                            }
                        }}
                        size="small"
                        style={{ maxWidth: 400 }}
                        items={sortedEstados.map(([id, est]) => {
                            const isCurrent = parseInt(id) === estado;
                            return {
                                title: '',
                                status: 'wait',
                                icon: (
                                    <Tooltip title={est.label}>
                                        <div
                                            style={{
                                                width: 28,
                                                height: 28,
                                                borderRadius: '50%',
                                                backgroundColor: isCurrent ? est.color : '#d9d9d9',
                                                color: 'white',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                fontWeight: 'bold',
                                                fontSize: 16
                                            }}
                                        >
                                            {id}
                                        </div>
                                    </Tooltip>
                                )
                            };
                        })}
                    />
                );
            }
        },
        {
            title: 'Total',
            dataIndex: 'ped_total',
            render: total => `$${Number(total).toFixed(2)}`
        },
        {
            title: 'Acciones',
            key: 'acciones',
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="Ver detalle">
                        <Button
                            type="text"
                            icon={<EyeOutlined style={{ fontSize: 18 }} />}
                            onClick={() => setPedidoSeleccionado(record)}
                            style={{ 
                                color: '#1890ff',
                                border: 'none',
                                boxShadow: 'none',
                                width: 28,
                                height: 28,
                                padding: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        />
                    </Tooltip>
                    <Tooltip title="Editar pedido">
                        <Button
                            type="text"
                            icon={<EditOutlined style={{ fontSize: 18 }} />}
                            onClick={() => setEditandoPedido(record)}
                            style={{ 
                                color: '#52c41a',
                                border: 'none',
                                boxShadow: 'none',
                                width: 28,
                                height: 28,
                                padding: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        />
                    </Tooltip>
                    <Tooltip title="Descargar PDF">
                        <Button
                            type="text"
                            icon={<FilePdfOutlined style={{ fontSize: 18 }} />}
                            onClick={() => {
                                setGenerandoPDFId(record.ped_id);
                                CGenerarPDF(record, tipoPagos, () => setGenerandoPDFId(null));
                            }}
                            loading={generandoPDFId === record.ped_id}
                            style={{ 
                                color: '#ff4d4f',
                                border: 'none',
                                boxShadow: 'none',
                                width: 28,
                                height: 28,
                                padding: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        />
                    </Tooltip>
                </Space>
            )
        }

    ];

    return (
        <div style={{ padding: 16 }}>
            <Modal open={cambiandoEstado} footer={null} closable={false} maskClosable={false} centered>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Spin />
                    <Text>Actualizando estado del pedido...</Text>
                </div>
            </Modal>
            {pedidos.length === 0 && !loading ? (
                <Empty description="No hay pedidos en esta sucursal" />
            ) : (
                <>
                    <Table
                        columns={columnas}
                        dataSource={pedidos}
                        loading={loading}
                        pagination={false}
                        rowKey="ped_id"
                        scroll={{ x: 'max-content' }}
                    />
                    <Pagination
                        current={paginaActual}
                        total={total}
                        pageSize={10}
                        onChange={fetchPedidos}
                        style={{ marginTop: 16, textAlign: 'right' }}
                    />
                </>
            )}

            <Modal
                open={!!pedidoSeleccionado}
                onCancel={() => setPedidoSeleccionado(null)}
                footer={null}
                title="Detalle del pedido"
                width="100%"
                style={{ top: 20 }}
            >
                {pedidoSeleccionado && (
                    <Row gutter={[16, 16]}>
                        <Col xs={24}>
                            <Card title="Artículos del pedido" style={{ marginBottom: 16 }}>
                                {pedidoSeleccionado.detalles?.map((item, index) => (
                                    <div key={index} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                                        <div>
                                            <Text strong>{item.aem_nombre}</Text><br />
                                            <Text type="secondary">x{item.dpe_cantidad}</Text>
                                        </div>
                                        <Text>${Number(item.dpe_total).toFixed(2)}</Text>
                                    </div>
                                ))}
                                <Divider />
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Text strong>Total:</Text>
                                    <Text strong>${Number(pedidoSeleccionado.ped_total).toFixed(2)}</Text>
                                </div>
                            </Card>

                            <Card title="Datos del pedido" style={{ marginBottom: 16 }}>
                                <p><Text strong>Fecha:</Text> {new Date(pedidoSeleccionado.ped_fecha).toLocaleString()}</p>
                                <p>
                                    <Text strong>Estado:</Text>{' '}
                                    <Tag color={estados[pedidoSeleccionado.ped_estado]?.color}>
                                        {estados[pedidoSeleccionado.ped_estado]?.label}
                                    </Tag>
                                </p>
                                <p>
                                    <Text strong>Forma de pago:</Text>{' '}
                                    {getFormaPagoLabel(pedidoSeleccionado, tipoPagos)}
                                </p>
                                <p>
                                    <Text strong>Tipo de envío:</Text>{' '}
                                    {pedidoSeleccionado.ped_tipoenvio === 2
                                        ? 'Retiro en local'
                                        : 'Envío a domicilio'}
                                </p>
                                {pedidoSeleccionado.ped_criterioreemplazo && (
                                    <p>
                                        <Text strong>Criterio de reemplazo:</Text>{' '}
                                        {
                                            criteriosReemplazo.find(c => c.cre_id === pedidoSeleccionado.ped_criterioreemplazo)
                                                ?.cre_descripcion || 'N/A'
                                        }
                                    </p>
                                )}
                                {pedidoSeleccionado.ped_productos_solicitados && (
                                    <p>
                                        <Text strong>Productos solicitados:</Text>{' '}
                                        <span style={{ whiteSpace: 'pre-wrap' }}>
                                            {pedidoSeleccionado.ped_productos_solicitados}
                                        </span>
                                    </p>
                                )}
                                {pedidoSeleccionado.ped_observaciones && (
                                    <p>
                                        <Text strong>Observaciones:</Text>{' '}
                                        <span style={{ whiteSpace: 'pre-wrap' }}>
                                            {pedidoSeleccionado.ped_observaciones}
                                        </span>
                                    </p>
                                )}
                            </Card>

                            <Card title="Datos del cliente">
                                <p><Text strong>Nombre:</Text> {pedidoSeleccionado.cli_nombre}</p>
                                <p>
                                    <Text strong>Teléfono:</Text>{' '}
                                    {(pedidoSeleccionado.cliente_telefono ?? pedidoSeleccionado.cli_telefono) || '—'}
                                </p>
                                <p><Text strong>Dirección:</Text> {pedidoSeleccionado.dci_direccion}</p>
                                <p><Text strong>Localidad:</Text> {pedidoSeleccionado.dci_localidad}</p>
                                <p><Text strong>Código postal:</Text> {pedidoSeleccionado.dci_codigoposta}</p>
                                {pedidoSeleccionado.dci_observacion && (
                                    <p><Text strong>Observación:</Text> {pedidoSeleccionado.dci_observacion}</p>
                                )}
                            </Card>
                        </Col>
                    </Row>

                )}
            </Modal>
            <CEditarPedido
                visible={!!editandoPedido}
                pedido={editandoPedido}
                onClose={() => setEditandoPedido(null)}
                onGuardarCambios={() => fetchPedidos(paginaActual)}
            />
        </div>
    );
};

export default CPedidosSucursal;